import { createBrowserRouter } from 'react-router-dom';
import { lazy } from 'react';
import LazyLoading from '@/lazy-loading.tsx';
import RouteErrorBoundary from '@/components/route-error-boundary.tsx';

//auth routes
const AuthLayout = lazy(() => import('@/layouts/auth/auth-layout.tsx'));
const SignUp = lazy(() => import('@/pages/auth/sign-up'));
const ForgetPassword = lazy(() => import('@/pages/auth/forget-password.tsx'));
const ResetPassword = lazy(() => import('@/pages/auth/reset-password.tsx'));
const VerifyEmailNotice = lazy(
  () => import('@/pages/auth/verify-email-notice.tsx'),
);
const VerifyEmailFailed = lazy(
  () => import('@/pages/auth/verify-email-failed.tsx'),
);
const VerifyEmailCallback = lazy(
  () => import('@/pages/auth/verify-email-callback.tsx'),
);
const Authenticate = lazy(() => import('@/pages/auth/authenticate'));
const GoogleCallback = lazy(() => import('@/pages/auth/google-callback.tsx'));
const RedirectIfAuthenticated = lazy(
  () => import('@/pages/auth/redirect-if-authenticated.tsx'),
);
const RedirectIfAuthorized = lazy(
  () => import('@/pages/auth/redirect-if-authorized.tsx'),
);
const Login = lazy(() => import('@/pages/auth/login.tsx'));

const ProtectedRoute = lazy(() => import('@/pages/auth/protected-route.tsx'));
const ForbiddenRoute = lazy(() => import('@/pages/auth/forbidden-route.tsx'));

const RecruitmentLayout = lazy(
  () => import('@/layouts/recruitment/recruitment-layout.tsx'),
);
const RecruitmentDefaultRedirect = lazy(
  () =>
    import('@/layouts/recruitment/components/recruitment-default-redirect.tsx'),
);
const CandidacyLayout = lazy(
  () => import('@/layouts/candidates/candidates-layout.tsx'),
);

//recruiter vacancies routes
const Vacancies = lazy(() => import('@/pages/recruitment/vacancies'));
const CreateVacancy = lazy(
  () => import('@/pages/recruitment/vacancies/create-vacancy.tsx'),
);
const VacancyDetails = lazy(
  () => import('@/pages/recruitment/vacancies/vacancy-details'),
);
const EditVacancy = lazy(
  () => import('@/pages/recruitment/vacancies/edit-vacancy.tsx'),
);

//candidate vacancies routes
const CandidateVacancies = lazy(
  () => import('@/pages/candidates/candidate-vacancies'),
);
const CandidateVacancyDetails = lazy(
  () =>
    import(
      '@/pages/candidates/candidate-vacancies/candidate-vacancy-details.tsx'
    ),
);
const CandidateApplications = lazy(
  () => import('@/pages/candidates/candidate-applications'),
);
const CandidateApplicationDetails = lazy(
  () =>
    import(
      '@/pages/candidates/candidate-applications/candidate-application-details.tsx'
    ),
);

//candidates routes
const Candidates = lazy(() => import('@/pages/recruitment/candidates'));
const CandidateDetails = lazy(
  () => import('@/pages/recruitment/candidates/candidate-details.tsx'),
);

const Applications = lazy(() => import('@/pages/recruitment/applications'));
const ApplicationDetails = lazy(
  () => import('@/pages/recruitment/applications/application-details.tsx'),
);

const Batches = lazy(() => import('@/pages/recruitment/batches'));
const CreateBatch = lazy(
  () => import('@/pages/recruitment/batches/create-batch.tsx'),
);
const EditBatch = lazy(
  () => import('@/pages/recruitment/batches/edit-batch.tsx'),
);

const InterviewSchedules = lazy(
  () => import('@/pages/recruitment/interviewSchedules'),
);
const Evaluations = lazy(() => import('@/pages/recruitment/evaluations'));
const TakeEvaluation = lazy(
  () => import('@/pages/recruitment/evaluations/take-evaluation'),
);
const EditEvaluation = lazy(
  () => import('@/pages/recruitment/evaluations/edit-evaluation'),
);
const ViewEvaluation = lazy(
  () => import('@/pages/recruitment/evaluations/view-evaluation'),
);
const AssessmentResults = lazy(
  () => import('@/pages/recruitment/assessment-results'),
);
const ViewAssessmentResult = lazy(
  () => import('@/pages/recruitment/assessment-results/view-assessment-result'),
);
const EvaluateAssessment = lazy(
  () => import('@/pages/recruitment/assessment-results/evaluate-assessment'),
);
const CandidateScores = lazy(
  () => import('@/pages/recruitment/candidate-scores'),
);
const LoginAsCandidate = lazy(
  () => import('@/pages/recruitment/login-as-candidate'),
);
const LoginAsEmployee = lazy(
  () => import('@/pages/recruitment/login-as-employee'),
);

const FormAuthoringForms = lazy(
  () => import('@/pages/recruitment/form-authoring'),
);
const FormAuthoringEditor = lazy(
  () => import('@/pages/recruitment/form-authoring/form-editor.tsx'),
);
const FormAuthoringQuestionBank = lazy(
  () => import('@/pages/recruitment/form-authoring/question-bank.tsx'),
);

//profile routes
const Profile = lazy(() => import('@/pages/candidates/profile'));
const EditProfile = lazy(
  () => import('@/pages/candidates/profile/edit-profile.tsx'),
);

//candidate assessments routes
const CandidateAssessments = lazy(
  () => import('@/pages/candidates/candidate-assessments'),
);
const TakeAssessment = lazy(
  () => import('@/pages/candidates/candidate-assessments/take-assessment.tsx'),
);

//errors routes
const Forbidden = lazy(() => import('@/pages/errors/forbidden.tsx'));
const NotFound = lazy(() => import('@/pages/errors/not-found.tsx'));

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <LazyLoading>
        <RedirectIfAuthorized>
          <CandidacyLayout />
        </RedirectIfAuthorized>
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <LazyLoading>
            <CandidateVacancies />
          </LazyLoading>
        ),
      },
      {
        path: 'vacancies/:id',
        element: (
          <LazyLoading>
            <CandidateVacancyDetails />
          </LazyLoading>
        ),
      },
    ],
  },

  {
    path: '/authenticate',
    element: (
      <LazyLoading>
        <Authenticate />
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/email/verify/:id/:hash',
    element: (
      <LazyLoading>
        <VerifyEmailCallback />
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/auth/google-callback',
    element: (
      <LazyLoading>
        <GoogleCallback />
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/auth',
    element: (
      <LazyLoading>
        <RedirectIfAuthenticated>
          <AuthLayout />
        </RedirectIfAuthenticated>
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/auth/login',
        element: (
          <LazyLoading>
            <Login />
          </LazyLoading>
        ),
      },
      {
        path: 'sign-up',
        element: (
          <LazyLoading>
            <SignUp />
          </LazyLoading>
        ),
      },
      {
        path: 'forget-password',
        element: (
          <LazyLoading>
            <ForgetPassword />
          </LazyLoading>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <LazyLoading>
            <ResetPassword />
          </LazyLoading>
        ),
      },
      {
        path: 'verify-email',
        element: (
          <LazyLoading>
            <VerifyEmailNotice />
          </LazyLoading>
        ),
      },
      {
        path: 'verify-email-failed',
        element: (
          <LazyLoading>
            <VerifyEmailFailed />
          </LazyLoading>
        ),
      },
    ],
  },
  {
    path: '/recruitment',
    element: (
      <LazyLoading>
        <ProtectedRoute>
          <ForbiddenRoute allowedRoles={['recruiter']}>
            <RecruitmentLayout />
          </ForbiddenRoute>
        </ProtectedRoute>
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <LazyLoading>
            <RecruitmentDefaultRedirect />
          </LazyLoading>
        ),
      },
      {
        path: 'candidates',
        element: (
          <LazyLoading>
            <Candidates />
          </LazyLoading>
        ),
      },
      {
        path: 'candidates/:id',
        element: (
          <LazyLoading>
            <CandidateDetails />
          </LazyLoading>
        ),
      },
      {
        path: 'vacancies',
        element: (
          <LazyLoading>
            <Vacancies />
          </LazyLoading>
        ),
      },
      {
        path: 'vacancies/create',
        element: (
          <LazyLoading>
            <CreateVacancy />
          </LazyLoading>
        ),
      },
      {
        path: 'vacancies/:id',
        element: (
          <LazyLoading>
            <VacancyDetails />
          </LazyLoading>
        ),
      },
      {
        path: 'vacancies/:id/edit',
        element: (
          <LazyLoading>
            <EditVacancy />
          </LazyLoading>
        ),
      },
      {
        path: 'batches',
        element: (
          <LazyLoading>
            <Batches />
          </LazyLoading>
        ),
      },
      {
        path: 'batches/create',
        element: (
          <LazyLoading>
            <CreateBatch />
          </LazyLoading>
        ),
      },
      {
        path: 'batches/:id/edit',
        element: (
          <LazyLoading>
            <EditBatch />
          </LazyLoading>
        ),
      },
      {
        path: 'applications',
        element: (
          <LazyLoading>
            <Applications />
          </LazyLoading>
        ),
      },
      {
        path: 'applications/:id',
        element: (
          <LazyLoading>
            <ApplicationDetails />
          </LazyLoading>
        ),
      },
      {
        path: 'interview-schedules',
        element: (
          <LazyLoading>
            <InterviewSchedules />
          </LazyLoading>
        ),
      },
      {
        path: 'evaluations',
        element: (
          <LazyLoading>
            <Evaluations />
          </LazyLoading>
        ),
      },
      {
        path: 'evaluations/:evaluationId/take',
        element: (
          <LazyLoading>
            <TakeEvaluation />
          </LazyLoading>
        ),
      },
      {
        path: 'evaluations/:evaluationId/edit',
        element: (
          <LazyLoading>
            <EditEvaluation />
          </LazyLoading>
        ),
      },
      {
        path: 'evaluations/:evaluationId/view',
        element: (
          <LazyLoading>
            <ViewEvaluation />
          </LazyLoading>
        ),
      },
      {
        path: 'assessment-results',
        element: (
          <LazyLoading>
            <AssessmentResults />
          </LazyLoading>
        ),
      },
      {
        path: 'assessment-results/:assessmentId/view',
        element: (
          <LazyLoading>
            <ViewAssessmentResult />
          </LazyLoading>
        ),
      },
      {
        path: 'assessment-results/:assessmentId/evaluate',
        element: (
          <LazyLoading>
            <EvaluateAssessment />
          </LazyLoading>
        ),
      },
      {
        path: 'candidate-scores',
        element: (
          <LazyLoading>
            <CandidateScores />
          </LazyLoading>
        ),
      },
      {
        path: 'login-as-candidate',
        element: (
          <LazyLoading>
            <LoginAsCandidate />
          </LazyLoading>
        ),
      },
      {
        path: 'login-as-employee',
        element: (
          <LazyLoading>
            <LoginAsEmployee />
          </LazyLoading>
        ),
      },
      {
        path: 'forms',
        element: (
          <LazyLoading>
            <FormAuthoringForms />
          </LazyLoading>
        ),
      },
      {
        path: 'questions',
        element: (
          <LazyLoading>
            <FormAuthoringQuestionBank />
          </LazyLoading>
        ),
      },
      {
        path: 'forms/:formId',
        element: (
          <LazyLoading>
            <FormAuthoringEditor />
          </LazyLoading>
        ),
      },
    ],
  },
  {
    path: '/applications',
    element: (
      <LazyLoading>
        <ProtectedRoute>
          <ForbiddenRoute allowedRoles={['candidate']}>
            <CandidacyLayout />
          </ForbiddenRoute>
        </ProtectedRoute>
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <LazyLoading>
            <CandidateApplications />
          </LazyLoading>
        ),
      },
      {
        path: ':id',
        element: (
          <LazyLoading>
            <CandidateApplicationDetails />
          </LazyLoading>
        ),
      },
    ],
  },
  {
    path: '/profile',
    element: (
      <LazyLoading>
        <ProtectedRoute>
          <ForbiddenRoute allowedRoles={['candidate']}>
            <CandidacyLayout />
          </ForbiddenRoute>
        </ProtectedRoute>
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <LazyLoading>
            <Profile />
          </LazyLoading>
        ),
      },
      {
        path: 'edit',
        element: (
          <LazyLoading>
            <EditProfile />
          </LazyLoading>
        ),
      },
    ],
  },
  {
    path: '/assessments',
    element: (
      <LazyLoading>
        <ProtectedRoute>
          <ForbiddenRoute allowedRoles={['candidate']}>
            <CandidacyLayout />
          </ForbiddenRoute>
        </ProtectedRoute>
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <LazyLoading>
            <CandidateAssessments />
          </LazyLoading>
        ),
      },
      {
        path: ':assessmentId',
        element: (
          <LazyLoading>
            <TakeAssessment />
          </LazyLoading>
        ),
      },
    ],
  },

  {
    path: '/forbidden',
    element: (
      <LazyLoading>
        <Forbidden />
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '*',
    element: (
      <LazyLoading>
        <NotFound />
      </LazyLoading>
    ),
    errorElement: <RouteErrorBoundary />,
  },
]);

export default router;
